import type { CSSProperties } from 'react'

import type { VerBlock } from '../spec/types'

/**
 * The version editor — a history rail beside a diff against what is live.
 *
 * Verbatim from `docs/design/reference/blocks/ver.html`. This is the only way
 * the console changes a versioned row: catalog entries, risk typologies and
 * price lists are edited by authoring a NEW version with an `effective_from`,
 * never by writing over the row that is live (law 6, invariant 9). Nothing in
 * this block is an in-place edit affordance, by construction — the left rail is
 * read-only history, and the single write is "Save as new version".
 *
 * Two things this block exists to make unmissable:
 *
 *   - The diff is always stated against LIVE, with a change class per line, so
 *     an operator sees that widening a remittance pattern or raising a fee is a
 *     different kind of act from a neutral relabel before they commit to it.
 *   - The notice-period gate sits between the diff and the button, not after
 *     it. A price increase carries a legally required notice period, and the
 *     sentence explaining why the earliest permitted date is what it is has to
 *     be read before the date is accepted — not discovered in a rejection.
 *
 * No amount is formatted here. The diff's live and proposed values are already
 * rendered by the corpus with their ISO code adjacent ("9.90 EUR", "0.85 %"),
 * because a diff cell may hold a rate, a duration or a set size as readily as
 * money; re-parsing them into figures here is how a currency gets dropped
 * (invariant 4).
 */

interface Props {
  readonly block: VerBlock
  /**
   * Whether the acting role, or a standby cell, forbids the write. The screen
   * projector computes this once for every block on the screen; here it dims
   * the save control and genuinely disables it, so a dead button never reads
   * as an available one.
   */
  readonly writeBlocked: boolean
  /** The screen's one-line explanation of the write posture, shown beneath. */
  readonly writeNote: string
}

export function Ver({ block, writeBlocked, writeNote }: Props) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(230px,1fr) 2fr',
        gap: '1px',
        background: 'var(--k-line)',
        border: '1px solid var(--k-line)',
      }}
    >
      <div style={{ background: 'var(--k-panel)' }}>
        {block.versions.map((v) => (
          // `data-cur` marks the version under edit — the draft at the top of
          // the rail. It is the same cursor treatment tables use, so "the row
          // you are acting on" looks identical everywhere in the console.
          <div
            key={v[0]}
            data-cur={v[5] === true ? '1' : ''}
            style={{ padding: '8px 11px', borderBottom: '1px solid var(--k-line)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontFamily: 'var(--k-mono)', fontSize: '11.5px', fontWeight: 600 }}>
                {v[0]}
              </span>
              <span
                data-tone={v[2]}
                style={{
                  padding: '0 5px',
                  fontSize: '9.5px',
                  fontWeight: 800,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                }}
              >
                {v[1]}
              </span>
            </div>
            <div
              style={{
                fontSize: '11px',
                color: 'var(--k-mut)',
                marginTop: '3px',
                fontFamily: 'var(--k-mono)',
              }}
            >
              {v[3]}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--k-mut2)', marginTop: '2px' }}>{v[4]}</div>
          </div>
        ))}
      </div>

      <div style={{ background: 'var(--k-panel)', padding: '11px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '9px' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 800,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
            }}
          >
            Diff against live
          </span>
          <span style={{ fontFamily: 'var(--k-mono)', fontSize: '11px', color: 'var(--k-mut)' }}>
            {block.diffRef ?? ''}
          </span>
        </div>

        <div style={DIFF_TRACKS}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'var(--gtc)',
              borderBottom: '2px solid var(--k-rule)',
              paddingBottom: '4px',
              fontSize: '9.5px',
              letterSpacing: '.07em',
              textTransform: 'uppercase',
              color: 'var(--k-mut)',
            }}
          >
            <span>Item</span>
            <span>Live</span>
            <span>Proposed</span>
            <span>Class</span>
          </div>
          {block.diff.map((d) => (
            <div
              key={d[0]}
              style={{
                display: 'grid',
                gridTemplateColumns: 'var(--gtc)',
                alignItems: 'center',
                borderBottom: '1px solid var(--k-line)',
                padding: '5px 0',
                fontSize: '12px',
              }}
            >
              <span style={{ fontFamily: 'var(--k-mono)', fontSize: '11.5px' }}>{d[0]}</span>
              <span style={{ color: 'var(--k-mut)' }}>{d[1]}</span>
              <span style={{ fontWeight: 600 }}>{d[2]}</span>
              <span
                data-tone={d[4]}
                style={{
                  justifySelf: 'start',
                  padding: '0 5px',
                  fontSize: '10px',
                  fontWeight: 800,
                  letterSpacing: '.04em',
                  textTransform: 'uppercase',
                }}
              >
                {d[3]}
              </span>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: '12px',
            border: '1px solid var(--k-line)',
            background: 'var(--k-bg)',
            padding: '10px',
          }}
        >
          <div
            style={{
              fontSize: '10.5px',
              letterSpacing: '.07em',
              textTransform: 'uppercase',
              color: 'var(--k-mut2)',
            }}
          >
            Effective from — notice period gate
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '12px',
              marginTop: '7px',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ fontSize: '10.5px', color: 'var(--k-mut)', marginBottom: '3px' }}>
                effective_from
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {/* Read-only, as in the prototype, whose change handler is a
                    no-op: the date the gate computes is the one that matters,
                    and nothing here may quietly become the source of it. The
                    visible key above is not programmatically associated with
                    the field, so the label is repeated for a screen reader. */}
                <input
                  value={block.effFrom}
                  readOnly
                  aria-label="effective_from"
                  style={{
                    height: '26px',
                    width: '118px',
                    border: '1px solid var(--k-line)',
                    background: 'var(--k-panel)',
                    color: 'var(--k-ink)',
                    fontFamily: 'var(--k-mono)',
                    fontSize: '12px',
                    padding: '0 7px',
                    borderRadius: 0,
                  }}
                />
                <span
                  style={{
                    height: '26px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 7px',
                    border: '1px solid var(--k-line)',
                    borderLeft: 0,
                    fontSize: '10.5px',
                    color: 'var(--k-mut)',
                    background: 'var(--k-panel2)',
                  }}
                >
                  earliest permitted
                </span>
              </div>
            </div>
            <div
              style={{
                fontSize: '11.5px',
                color: 'var(--k-mut)',
                maxWidth: '44ch',
                textWrap: 'pretty',
              }}
            >
              {block.gate}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '12px',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              disabled={writeBlocked}
              data-dim={writeBlocked ? '1' : ''}
              style={{
                height: '28px',
                padding: '0 12px',
                border: 0,
                background: 'var(--k-accent)',
                color: 'var(--k-accentink)',
                font: 'inherit',
                fontWeight: 800,
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Save as new version
            </button>
            <button
              type="button"
              className="k-hov"
              style={{
                height: '28px',
                padding: '0 12px',
                border: '1px solid var(--k-line)',
                background: 'transparent',
                color: 'var(--k-ink)',
                font: 'inherit',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Preview disclosure
            </button>
            {/* Blast radius sits beside the button, never behind a confirm
                step: how many customers a version reaches, and in which
                languages they get told, is part of deciding to save it. */}
            <span style={{ fontSize: '11.5px', color: 'var(--k-mut)' }}>{block.blast}</span>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--k-mut2)', marginTop: '6px' }}>
            {writeNote}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The diff's column tracks, carried as a custom property exactly as the guide
 * does, so the header and every row read the same four widths from one place.
 */
const DIFF_TRACKS = { '--gtc': '1.6fr .9fr .9fr .8fr' } as CSSProperties
